import React from 'react';
import { db } from '../data/store';
import { LOGO_BASE64 } from '../data/logo';

const text = value => String(value ?? '').trim();
const lower = value => text(value).toLowerCase();
const escapeHtml = value => text(value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
const rowKey = row => `${row.sampleId}\u0000${row.parameter}`;
const hasValue = value => value !== undefined && value !== null && text(value) !== '';
const displayResult = row => {
  if (row.verdict === 'less') return `Less Than ${row.value}`;
  if (row.verdict === 'more') return `Greater Than ${row.value}`;
  return text(row.value);
};

const buildRows = samples => samples.flatMap(sample => {
  const resultMap = sample.results || {};
  const parameters = [...new Set([...(sample.params || []), ...Object.keys(resultMap)])];
  return parameters.flatMap(parameter => {
    const result = resultMap[parameter];
    if (!result || !hasValue(result.value)) return [];
    return [{
      sampleId:String(sample.id),
      workOrder:sample.workOrder || '',
      // `source` is the company name. Older records use `client` for the
      // contact person, so only fall back to it when no source was stored.
      company:sample.source || sample.company || sample.client || '',
      description:sample.desc || sample.description || '',
      parameter,
      value:result.value,
      verdict:result.verdict || result.qual || '',
      unit:result.unit || '',
      dl:result.detectionLimit ?? result.methodDetectionLimit ?? result.mdl ?? result.dl ?? '',
      rl:result.reportingLimit ?? result.rdl ?? '',
      method:result.method || '',
      date:result.dateTested || sample.dateCompleted || sample.received || '',
      tech:result.tech || sample.tech || '',
    }];
  });
});

function printRows(rows, filters) {
  if (!rows.length) return;
  const generated = new Date().toLocaleString();
  const bodyRows = rows.map(row => `<tr>
    <td>${escapeHtml(row.sampleId)}</td><td>${escapeHtml(row.workOrder)}</td>
    <td>${escapeHtml(row.company)}</td><td>${escapeHtml(row.description)}</td>
    <td>${escapeHtml(row.parameter)}</td><td>${escapeHtml(displayResult(row))}</td>
    <td>${escapeHtml(row.unit)}</td><td>${escapeHtml(row.dl)}</td><td>${escapeHtml(row.rl)}</td>
    <td>${escapeHtml(row.method)}</td><td>${escapeHtml(row.date)}</td><td>${escapeHtml(row.tech)}</td>
  </tr>`).join('');
  const popup = window.open('', '_blank');
  if (!popup) return;
  popup.document.write(`<!doctype html><html><head><title>Result History</title><style>
    @page{size:landscape;margin:0.35in}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:0}
    .head{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #1e3060;padding-bottom:7px;margin-bottom:8px}
    .head img{height:42px}.title{text-align:center;flex:1}.title h1{font-size:20px;margin:0;color:#1e3060}.title div{font-size:10px;margin-top:3px}
    .meta{font-size:9px;text-align:right}.filters{font-size:10px;margin:5px 0 9px}table{width:100%;border-collapse:collapse;font-size:8.5px}
    th{background:#20386d;color:white;padding:4px;border:1px solid #53658e;text-align:left}td{padding:4px;border:1px solid #cdd5e2;vertical-align:top}
    tbody tr:nth-child(even){background:#f6f8fc}
  </style></head><body>
    <div class="head"><img src="${LOGO_BASE64}"/><div class="title"><h1>Analytical Result History</h1><div>${rows.length} selected historical result${rows.length === 1 ? '' : 's'}</div></div><div class="meta">Printed<br/>${escapeHtml(generated)}</div></div>
    <div class="filters"><strong>Company:</strong> ${escapeHtml(filters.company || 'All')} &nbsp; <strong>Parameter:</strong> ${escapeHtml(filters.parameter || 'All')} &nbsp; <strong>Description search:</strong> ${escapeHtml(filters.description || '—')}</div>
    <table><thead><tr><th>Sample</th><th>Work Order</th><th>Company</th><th>Description</th><th>Parameter</th><th>Result</th><th>Unit</th><th>DL</th><th>RL</th><th>Method</th><th>Date Analyzed</th><th>Tech</th></tr></thead><tbody>${bodyRows}</tbody></table>
  </body></html>`);
  popup.document.close();
  popup.onload = () => popup.print();
}

export default function History({ toast }) {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [company, setCompany] = React.useState('');
  const [parameter, setParameter] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [selected, setSelected] = React.useState(new Set());

  React.useEffect(() => {
    const timer = setInterval(() => setRefreshTick(value => value + 1), 3000);
    return () => clearInterval(timer);
  }, []);

  const samples = React.useMemo(() => db.getSamples(), [refreshTick]);
  const allRows = React.useMemo(() => buildRows(samples), [samples]);
  const companies = React.useMemo(() => [...new Set(allRows.map(row => row.company).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b)), [allRows]);
  const companyRows = React.useMemo(() => company
    ? allRows.filter(row => lower(row.company) === lower(company))
    : allRows, [allRows, company]);
  const parameters = React.useMemo(() => [...new Set(companyRows.map(row => row.parameter).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b)), [companyRows]);

  React.useEffect(() => {
    if (parameter && !parameters.includes(parameter)) setParameter('');
  }, [company, parameters, parameter]);

  const rows = React.useMemo(() => companyRows
    .filter(row => !parameter || row.parameter === parameter)
    .filter(row => !description || lower(row.description).includes(lower(description)))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)) ||
      String(b.sampleId).localeCompare(String(a.sampleId), undefined, {numeric:true})),
  [companyRows, parameter, description]);

  const toggle = key => setSelected(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const visibleKeys = rows.map(rowKey);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every(key => selected.has(key));
  const selectedRows = allRows.filter(row => selected.has(rowKey(row)));
  const handlePrint = () => {
    if (!selectedRows.length) {
      toast('error', 'Select at least one historical result to print');
      return;
    }
    printRows(selectedRows, {company, parameter, description});
  };

  const th = {position:'sticky',top:0,zIndex:2,background:'#20386d',color:'white',padding:'7px 8px',borderRight:'1px solid #53658e',textAlign:'left',whiteSpace:'nowrap'};
  const td = {padding:'6px 8px',borderRight:'1px solid #dce2ec',borderBottom:'1px solid #dce2ec',verticalAlign:'top'};

  return <div style={{height:'100%',display:'flex',flexDirection:'column',minHeight:0,background:'#f5f7fb'}}>
    <div className="toolbar" style={{gap:8,flexWrap:'wrap'}}>
      <strong style={{color:'#1e3060',fontSize:13}}>📚 Result History</strong>
      <div className="tb-sep"/>
      <label style={{fontSize:10,fontWeight:700,color:'#52617a'}}>Company</label>
      <select value={company} onChange={event => { setCompany(event.target.value); setSelected(new Set()); }} style={{minWidth:220,padding:'5px 7px'}}>
        <option value="">All companies</option>{companies.map(value => <option key={value}>{value}</option>)}
      </select>
      <label style={{fontSize:10,fontWeight:700,color:'#52617a'}}>Parameter</label>
      <select value={parameter} onChange={event => { setParameter(event.target.value); setSelected(new Set()); }} style={{minWidth:250,padding:'5px 7px'}}>
        <option value="">All parameters</option>{parameters.map(value => <option key={value}>{value}</option>)}
      </select>
      <input value={description} onChange={event => setDescription(event.target.value)} placeholder="Search sample description…" style={{width:230,padding:'6px 8px'}}/>
      <button className="btn" onClick={() => { setCompany(''); setParameter(''); setDescription(''); setSelected(new Set()); }}>Clear Filters</button>
      <div style={{flex:1}}/>
      <span style={{fontSize:11,color:'#52617a'}}>{rows.length} result{rows.length === 1 ? '' : 's'} · {selected.size} selected</span>
      <button className="btn btn-primary" onClick={handlePrint} disabled={!selected.size}>🖨 Print Selected ({selected.size})</button>
    </div>

    <div style={{padding:'8px 12px',background:'#eef5ff',borderBottom:'1px solid #c7d6ea',fontSize:11,color:'#294766'}}>
      Choose a company and parameter, search by description, then check the old results you need. Only checked rows are included in the printed history list.
    </div>

    <div style={{flex:1,overflow:'auto',margin:10,background:'white',border:'1px solid #d2dae7',borderRadius:5}}>
      <table style={{width:'100%',minWidth:1450,borderCollapse:'separate',borderSpacing:0,fontSize:11}}>
        <thead><tr>
          <th style={{...th,width:38,textAlign:'center'}}><input type="checkbox" checked={allVisibleSelected} onChange={() => setSelected(previous => {
            const next = new Set(previous); visibleKeys.forEach(key => allVisibleSelected ? next.delete(key) : next.add(key)); return next;
          })}/></th>
          {['Sample','Work Order','Company','Description','Parameter','Result','Unit','DL','RL','Method','Date Analyzed','Tech'].map(label => <th key={label} style={th}>{label}</th>)}
        </tr></thead>
        <tbody>{rows.map(row => {
          const key = rowKey(row);
          return <tr key={key} style={{background:selected.has(key)?'#fff7d6':'white'}}>
            <td style={{...td,textAlign:'center'}}><input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)}/></td>
            <td style={{...td,fontFamily:'monospace',fontWeight:700}}>{row.sampleId}</td><td style={td}>{row.workOrder}</td>
            <td style={td}>{row.company}</td><td style={{...td,maxWidth:260}}>{row.description || '—'}</td>
            <td style={{...td,fontWeight:700}}>{row.parameter}</td><td style={{...td,fontWeight:700,color:row.verdict==='less'?'#b83232':'inherit'}}>{displayResult(row)}</td>
            <td style={td}>{row.unit}</td><td style={td}>{row.dl}</td><td style={td}>{row.rl}</td><td style={td}>{row.method}</td><td style={td}>{row.date}</td><td style={td}>{row.tech}</td>
          </tr>;
        })}</tbody>
      </table>
      {!rows.length && <div style={{padding:28,textAlign:'center',color:'#7a8496'}}>No saved analyzed results match these filters.</div>}
    </div>
  </div>;
}
